import createNewCharacterItemEntry from '../helpers/createNewCharacterItemEntry'
import { getNormalisedEquipmentType } from '../helpers/getEffectiveItemType'
import getWeaponDamageDice from '../helpers/getWeaponDamageDice'
import trackItemChanges from '../helpers/trackItemChanges.js'
import loadTraits from '../helpers/loadTraits.js'

import processTagContainers from './processTagContainters.js'

import store from '../store/index'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function normaliseRaceValue(raw) {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'object') return raw.id ?? null
  if (typeof raw !== 'string') return raw

  const trimmed = raw.trim()
  if (!trimmed) return null
  if (['unknown', 'n/a', 'na', 'none'].includes(trimmed.toLowerCase())) return null
  if (UUID_RE.test(trimmed)) return trimmed

  const race = store.getters?.['races/getRaceByName']?.(trimmed)
  return race?.id ?? null
}

function normaliseCurrencyValue(raw) {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'object') return raw.id ?? null
  if (typeof raw !== 'string') return raw

  const trimmed = raw.trim()
  if (!trimmed) return null
  if (['unknown', 'n/a', 'na', 'none'].includes(trimmed.toLowerCase())) return null
  if (UUID_RE.test(trimmed)) return trimmed

  const currencies = store.getters?.['characters/getCurrencies'] ?? []
  const match = currencies.find(
    (currency) =>
      currency?.id === trimmed || currency?.name === trimmed || currency?.short_name === trimmed,
  )

  return match?.id ?? null
}

function formatValueNumber(value) {
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return ''
  }

  return Number.isInteger(numericValue)
    ? `${numericValue}`
    : numericValue.toFixed(2).replace(/\.?0+$/, '')
}

function getCurrencySuffix(rawCurrency) {
  if (rawCurrency === null || rawCurrency === undefined || rawCurrency === '') {
    return ''
  }

  if (typeof rawCurrency === 'object') {
    return rawCurrency.short_name || rawCurrency.name || rawCurrency.id || ''
  }

  const currencies = store.getters?.['characters/getCurrencies'] ?? []
  const match = currencies.find(
    (currency) =>
      currency &&
      (currency.id === rawCurrency ||
        currency.short_name === rawCurrency ||
        currency.name === rawCurrency),
  )

  return match?.short_name || match?.name || rawCurrency
}

function buildItemValueDisplay(item = {}) {
  const lowerNumericValue = Number(item?.value_lower)
  const upperNumericValue = Number(item?.value_upper)
  const hasLower =
    item?.value_lower !== null &&
    item?.value_lower !== undefined &&
    item?.value_lower !== '' &&
    Number.isFinite(lowerNumericValue) &&
    lowerNumericValue !== 0
  const hasUpper =
    item?.value_upper !== null &&
    item?.value_upper !== undefined &&
    item?.value_upper !== '' &&
    Number.isFinite(upperNumericValue) &&
    upperNumericValue !== 0

  if (!hasLower) {
    return ''
  }

  const lowerValue = formatValueNumber(item.value_lower)
  if (!lowerValue) {
    return ''
  }

  const upperValue = hasUpper ? formatValueNumber(item.value_upper) : ''
  const currencySuffix = getCurrencySuffix(item?.currency)
  const valueDisplay = upperValue
    ? `${lowerValue}-${upperValue}${currencySuffix}`
    : `${lowerValue}${currencySuffix}`

  return item?.estimated_value ? `${valueDisplay} (E)` : valueDisplay
}

function buildArtefactBaseItemDisplay(item = {}) {
  const rawBaseItem = item?.artefact_base_item

  if (rawBaseItem === null || rawBaseItem === undefined || rawBaseItem === '') {
    return ''
  }

  if (typeof rawBaseItem === 'object') {
    return rawBaseItem.name || rawBaseItem.display_name || rawBaseItem.label || ''
  }

  const staticBaseItem = store.getters?.['characters/getStaticItemById']?.(rawBaseItem)
  return staticBaseItem?.name || ''
}
//------------------
// process add item
//------------------
export default function processAddItem({
  parms = {},
  changed_items = [],
  original_items = [],
  character = null,
} = {}) {
  console.log('--------------------------------------')
  console.log('IN processAddItem parms = ', parms)
  //-----------
  // get parms
  //-----------
  const track_item = parms.payload ?? {} // includes temp id for tracking
  const static_item_id = parms.payload.item ?? null // could be temp id for artefacts as item obj to be created first in backend then linked to character-item
  const tally_qty = parms.payload.tally_qty ?? 0
  const player_notes = parms.payload.player_notes ?? parms.payload.notes ?? ''
  const value_lower = parms.payload.value_lower ?? 0
  const value_upper = parms.payload.value_upper ?? 0
  const equipment_type = getNormalisedEquipmentType(parms.payload.equipment_type)
  const estimated_value = parms.payload.estimated_value ?? false
  const worn_location = parms.payload.worn_location ?? null
  const attach_location = parms.payload.attach_location ?? null
  const armour_location = parms.payload.armour_location ?? null
  const container = parms.payload.container ?? null
  const runes = parms.payload.runes ?? []
  const non_rune_spells = parms.payload.non_rune_spells ?? []
  const recoverable_non_campaign_artefact = parms.payload.recoverable_non_campaign_artefact ?? false
  const backend_orchestrates_linked_value_updates = recoverable_non_campaign_artefact
  const backend_orchestrated_linked_value_update_source = recoverable_non_campaign_artefact
    ? 'recovery'
    : ''

  const starter = parms.payload.starter ?? false
  const active = parms.payload.active ?? false
  const campaign = parms.payload.campaign ?? false
  const tallyable = parms.payload.tallyable ?? false
  const countable = parms.payload.countable ?? false
  const wearable = parms.payload.wearable ?? false
  const storeable = parms.payload.storeable ?? false
  const held = parms.payload.held ?? false
  const worn = parms.payload.worn ?? false
  const storage = parms.payload.storage ?? false
  const attachable = parms.payload.attachable ?? false
  const attach_point = parms.payload.attach_point ?? false
  const broken = parms.payload.broken ?? false

  const short_description = parms.payload.short_description ?? ''
  const description = parms.payload.description ?? ''
  const enc = parms.payload.enc ?? 0
  const tot_enc = parms.payload.tot_enc ?? 0

  const container_size = parms.payload.container_size ?? null
  const container_capacity = parms.payload.container_capacity ?? ''
  const container_size_short = parms.payload.container_size_short ?? ''
  const container_size_long = parms.payload.container_size_long ?? ''

  const contents_count_direct = parms.payload.contents_count_direct ?? null
  const contents_count_total = parms.payload.contents_count_total ?? null

  const ap = parms.payload.ap ?? null
  const hp = parms.payload.hp ?? null
  const dex_pen = parms.payload.dex_pen ?? null
  const layer = parms.payload.layer ?? null
  const equipped = parms.payload.equipped ?? false

  let coverage = parms.payload.coverage ?? null
  const coverage_locations = parms.payload.coverage_locations ?? []

  const damage_dice = parms.payload.damage_dice ?? false
  const impale = parms.payload.impale ?? false
  const set_charge = parms.payload.set_charge ?? false
  const load = parms.payload.load ?? false
  const range = parms.payload.range ?? false

  const max_unit_count = parms.payload.max_unit_count ?? null
  const cur_unit_count = parms.payload.cur_unit_count ?? null
  const unit_name = parms.payload.unit_name ?? null
  const healing_type = parms.payload.healing_type ?? null
  const recovers = parms.payload.recovers ?? null
  const poison_type = parms.payload.poison_type ?? null
  const pot = parms.payload.pot ?? null
  const effect = parms.payload.effect ?? null
  const delay = parms.payload.delay ?? null
  const duration = parms.payload.duration ?? null
  const resist = parms.payload.resist ?? null
  const pwr_storage = parms.payload.pwr_storage ?? 0

  // artefact-specific lookups (safe to carry for all item types)
  const artefact_type = parms.payload.artefact_type ?? ''
  const artefact_base_item = parms.payload.artefact_base_item ?? null
  const trait = parms.payload.trait ?? null
  const material = parms.payload.material ?? ''
  const quality = parms.payload.quality ?? ''
  const age = parms.payload.age ?? ''
  const currency = parms.payload.currency ?? null
  const race = parms.payload.race ?? null
  const image_file = parms.payload.image_file ?? null
  const image = parms.payload.image ?? null
  const _static_material_unknown = parms.payload._static_material_unknown ?? undefined
  const _static_quality_unknown = parms.payload._static_quality_unknown ?? undefined
  const _static_age_unknown = parms.payload._static_age_unknown ?? undefined
  const _static_race_unknown = parms.payload._static_race_unknown ?? undefined
  const _static_currency_unknown = parms.payload._static_currency_unknown ?? undefined
  const _static_value_unknown = parms.payload._static_value_unknown ?? undefined
  const _static_value_lower_unknown = parms.payload._static_value_lower_unknown ?? undefined
  const _static_value_upper_unknown = parms.payload._static_value_upper_unknown ?? undefined
  const _static_estimated_value_readonly =
    parms.payload._static_estimated_value_readonly ?? undefined
  const _static_enc_readonly = parms.payload._static_enc_readonly ?? undefined
  const _static_countable_readonly = parms.payload._static_countable_readonly ?? undefined
  const _static_unit_name_readonly = parms.payload._static_unit_name_readonly ?? undefined
  const _static_max_unit_count_readonly = parms.payload._static_max_unit_count_readonly ?? undefined

  // where armour_location selected (non-fixed) - use specific coverage loc (e.g. Lower Left Leg, rather than Lower Leg)
  if (armour_location) {
    coverage = coverage_locations.find((loc) => loc.id === armour_location).name
    if (!coverage) {
      console.error(
        '*** Unable to find coverage loc for selected armour_location id:',
        armour_location,
      )
      return
    }
  }

  const character_id = character?.id ?? null

  console.log('item character_id = ', character_id)
  console.log('ITEM TO ADD = ', track_item)
  let calc_containers = []
  //-----------------
  // get static item
  //-----------------
  console.log('1. off to get static item')
  let static_item = static_item_id
    ? store.getters['characters/getStaticItemById'](static_item_id)
    : null
  console.log('##### static_item = ', static_item)

  const staticItemIsArtefactWeapon =
    static_item?.type === 'Artefact' &&
    (static_item?.artefact_type === 'Melee Weapon' ||
      static_item?.artefact_type === 'Ranged Weapon')
  const artefactWeaponSource =
    staticItemIsArtefactWeapon && Array.isArray(static_item?.skills) ? static_item : null

  const resolvedDamageDice =
    track_item.type === 'Melee Weapon' || track_item.type === 'Ranged Weapon'
      ? getWeaponDamageDice(artefactWeaponSource ?? static_item ?? parms.payload, character?.skills)
      : damage_dice
  const resolvedSkills =
    artefactWeaponSource?.skills ??
    static_item?.skills ??
    parms.payload.skills ??
    track_item.skills ??
    []
  //---------------------------------------------------------
  // indicate when this is a new artefact static item (no
  // backing Item record yet) so submission can use the
  // composite Item+CharacterItem endpoint
  //---------------------------------------------------------
  const create_static_item = !static_item && track_item.type === 'Artefact'

  if (create_static_item) {
    track_item.create_static_item = true
  }

  if (track_item.type === 'Artefact' && static_item) {
    track_item._initial_static_values = {
      artefact_type: static_item.artefact_type ?? '',
      trait: static_item.trait ?? null,
      material: static_item.material ?? '',
      quality: static_item.quality ?? '',
      age: static_item.age ?? '',
      race: normaliseRaceValue(static_item.race),
      currency: normaliseCurrencyValue(static_item.currency),
      value_lower: static_item.value_lower ?? 0,
      value_upper: static_item.value_upper ?? 0,
      estimated_value: static_item.estimated_value ?? false,
      description: static_item.description ?? '',
      enc: static_item.enc ?? 0,
      ap: static_item.ap ?? 0,
      hp: static_item.hp ?? 0,
      countable: static_item.countable ?? false,
      unit_name: static_item.unit_name ?? '',
      max_unit_count: static_item.max_unit_count ?? 0,
    }
  }
  //---------------
  // set item name
  //---------------
  const base_name = static_item?.name ?? parms.payload.name ?? '' // use static item name where possible, else payload name (for artefacts created via free text entry)
  track_item.name = base_name
  track_item.recoverable_non_campaign_artefact = recoverable_non_campaign_artefact
  track_item.backend_orchestrates_linked_value_updates = backend_orchestrates_linked_value_updates
  track_item.backend_orchestrated_linked_value_update_source =
    backend_orchestrated_linked_value_update_source

  const artefact_name = parms.payload.name ?? '' // use payload name for artefacts created via free text entry

  //------------------------------------------------------
  // if item name already exists - compute UI-only suffix
  //------------------------------------------------------
  let newDisplayName = null
  if (character && Array.isArray(character.items)) {
    // only consider items whose base name matches this static item
    const sameItems = character.items.filter((ci) => ci.name === base_name)

    if (sameItems.length >= 1) {
      // mirror backend behaviour: N existing -> new is "name N+1"
      newDisplayName = `${base_name} ${sameItems.length + 1}`
    }
  }
  //--------------------------------------------------------
  // ensure tracking payload carries display_name where set
  //--------------------------------------------------------
  if (newDisplayName && !track_item.display_name) {
    track_item.display_name = newDisplayName
  }
  //-------------------------------
  // update tracking with new item
  //-------------------------------
  console.log('2. off to track item changes')
  changed_items = trackItemChanges({
    item: track_item,
    original_items,
    changed_items,
    character_id,
  })
  //---------------------------------
  // create new character item entry
  //---------------------------------
  console.log('3. off to create new character item entry')
  let new_item = createNewCharacterItemEntry({
    id: track_item.id, // temp id used for tracking
    type: track_item.type,
    equipment_type: equipment_type,
    static_item: static_item,
    artefact_id: static_item_id, // used to indicate artefact/temp static item id for item objects to be created first in backend then linked to character-item
    artefact_name: artefact_name,
    recoverable_non_campaign_artefact: recoverable_non_campaign_artefact,
    backend_orchestrates_linked_value_updates: backend_orchestrates_linked_value_updates,
    backend_orchestrated_linked_value_update_source:
      backend_orchestrated_linked_value_update_source,
    tally_qty: tally_qty,
    player_notes: player_notes,
    value_lower: value_lower,
    value_upper: value_upper,
    estimated_value: estimated_value,
    worn_location: worn_location,
    attach_location: attach_location,
    armour_location: armour_location,
    container: container,
    runes: runes,
    non_rune_spells: non_rune_spells,
    character: character,
    starter: starter,
    active: active,
    campaign: campaign,
    tallyable: tallyable,
    countable: countable,
    wearable: wearable,
    storeable: storeable,
    held: held,
    storage: storage,
    attachable: attachable,
    attach_point: attach_point,
    worn: worn,
    broken: broken,
    short_description: short_description,
    enc: enc,
    tot_enc: tot_enc,
    display_name: newDisplayName,
    container_size: container_size,
    container_capacity: container_capacity,
    container_size_short: container_size_short,
    container_size_long: container_size_long,
    contents_count_direct: contents_count_direct,
    contents_count_total: contents_count_total,
    ap: ap,
    hp: hp,
    coverage: coverage,
    dex_pen: dex_pen,
    layer: layer,
    coverage_locations: coverage_locations,
    equipped: equipped,
    skills: resolvedSkills,
    damage_dice: resolvedDamageDice,
    impale: impale,
    set_charge: set_charge,
    load: load,
    range: range,
    max_unit_count: max_unit_count,
    cur_unit_count: cur_unit_count,
    unit_name: unit_name,
    healing_type: healing_type,
    recovers: recovers,
    poison_type: poison_type,
    pot: pot,
    effect: effect,
    delay: delay,
    duration: duration,
    resist: resist,
    pwr_storage: pwr_storage,
    artefact_type: artefact_type,
    artefact_base_item: artefact_base_item,
    trait: trait,
    material: material,
    quality: quality,
    age: age,
    currency: currency,
    race: race,
    description: description,
    image: image,
    item_traits: parms.payload.item_traits ?? [],
    image_file: image_file,
    create_static_item: create_static_item,
    _static_material_unknown: _static_material_unknown,
    _static_quality_unknown: _static_quality_unknown,
    _static_age_unknown: _static_age_unknown,
    _static_race_unknown: _static_race_unknown,
    _static_currency_unknown: _static_currency_unknown,
    _static_value_unknown: _static_value_unknown,
    _static_value_lower_unknown: _static_value_lower_unknown,
    _static_value_upper_unknown: _static_value_upper_unknown,
    _static_estimated_value_readonly: _static_estimated_value_readonly,
    _static_enc_readonly: _static_enc_readonly,
    _static_countable_readonly: _static_countable_readonly,
    _static_unit_name_readonly: _static_unit_name_readonly,
    _static_max_unit_count_readonly: _static_max_unit_count_readonly,
  })
  new_item.value = buildItemValueDisplay(new_item)
  new_item.base_item = buildArtefactBaseItemDisplay(new_item)
  console.log('character.items = ', character.items)
  //-----------------------------
  // add item to character items
  //-----------------------------
  character.items.push(new_item)
  console.log('NEW ITEM ADDED TO CHARACTER = ', new_item)
  character = loadTraits({ character })
  //-----------------------------------------------------------
  // check for container selected when item created
  // (NB: tot_enc for new item already calculated in itemcard)
  //-----------------------------------------------------------
  if (container) {
    console.log('new item is in container id = ', container)
    console.log('will need to recalc container contents for container id = ', container)
    //-------------------------------------------------------
    // go process tag containers (for calcs in main process)
    //-------------------------------------------------------
    calc_containers = processTagContainers({
      container_id: container,
      character_items: character.items,
    })
  } else {
    console.log('new item is not in a container - no need to recalc container contents')
  }
  //------------------------
  // return mutated objects
  //------------------------
  console.log('completed processAddItem new item = ', new_item)
  return {
    changed_items,
    new_item,
    character,
    calc_containers,
    equipped_armour: equipped,
    static_item,
  }
}
